import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ChevronLeft } from "lucide-react";
import { previewHeaders } from "../lib/previewAuth";
import FounderLinks from "./community/FounderLinks";
import { ReadingTime, estimateReadingMinutes } from "./ReadingTime";
import { ArticleAudioPlayer } from "./ArticleAudioPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Head-tag helpers (local; the Perspective page uses the same pattern).
const upsertMeta = (attr, key, value) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", value);
  return el;
};
const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
  return el;
};

// --- Body renderer. Kept intentionally small: only the block types this
// feature uses are handled. Anything else is silently skipped.
const Block = ({ block }) => {
  if (block.type === "heading") {
    const level = block.level || 2;
    const Tag = `h${Math.min(Math.max(level, 2), 4)}`;
    const cls = level === 2
      ? "text-2xl md:text-3xl font-serif tracking-tight text-primary-custom leading-tight mt-20 mb-8"
      : "text-xl md:text-2xl font-serif tracking-tight text-primary-custom leading-tight mt-14 mb-6";
    return <Tag className={cls}>{block.text}</Tag>;
  }
  if (block.type === "paragraph") {
    return (
      <p className="font-serif text-lg md:text-xl text-primary-custom font-light mb-8 !leading-[1.7]">
        {block.text}
      </p>
    );
  }
  if (block.type === "pullquote") {
    return (
      <blockquote className="my-16 py-8 border-t border-b border-[var(--accent-bronze)]/40" data-testid="founder-pullquote">
        <p className="text-2xl md:text-3xl lg:text-4xl font-serif italic text-primary-custom leading-snug tracking-tight max-w-3xl mx-auto text-center">
          {block.text}
        </p>
      </blockquote>
    );
  }
  if (block.type === "figure") {
    // In-body editorial figure. Portrait aspect is preserved (no crop). The
    // container escapes the body's max-w-2xl column so the picture can breathe
    // with generous whitespace above and below. "narrow" keeps the image
    // slightly tighter than the hero, giving the article a quieter second
    // photographic beat.
    const widthCls =
      block.size === "narrow" ? "max-w-xl" :
      block.size === "medium" ? "max-w-2xl" :
      block.size === "wide"   ? "max-w-3xl" :
                                "max-w-2xl";
    return (
      <figure
        className={`${widthCls} mx-auto my-24 md:my-32`}
        data-testid="founder-figure"
      >
        <div className="w-full flex justify-center">
          <img
            src={block.src}
            alt={block.alt || ""}
            loading="lazy"
            decoding="async"
            className="h-auto max-h-[80vh] w-auto"
          />
        </div>
        {block.caption && (
          <figcaption className="mt-8 md:mt-10 text-center font-serif italic text-sm md:text-base text-secondary-custom leading-relaxed">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  }
  return null;
};

export default function FounderFeature() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const tListen = t("listen.safetyNote");
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/community/${slug}?lang=en`, { headers: previewHeaders() })
      .then(res => { if (!cancelled) setItem(res.data); })
      .catch(err => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // Manage <title>, meta description, robots, canonical, and social tags on the fly.
  useEffect(() => {
    if (!item) return;
    const originalTitle = document.title;
    const isDraft = item.status !== "published";

    document.title = (item.seo && item.seo.title) || `${item.title} — The Thriving Life 101`;
    if (item.seo && item.seo.description) upsertMeta("name", "description", item.seo.description);

    const robots = upsertMeta("name", "robots", isDraft ? "noindex,nofollow" : "index,follow,max-image-preview:large");

    const url = `https://www.thethrivinglife101.com/community/${item.slug}`;
    const canonical = upsertLink("canonical", url);
    // Absolutise image references so schema.org, Facebook, Twitter, LinkedIn
    // and Slack all resolve identically. Consumers vary in how they resolve
    // a relative path against `og:url`; absolute URLs remove the ambiguity.
    const CANONICAL_ORIGIN = "https://www.thethrivinglife101.com";
    const toAbs = (u) => (!u ? null : (u.startsWith("http") ? u : `${CANONICAL_ORIGIN}${u}`));

    upsertMeta("property", "og:type", "article");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:title", (item.seo && item.seo.title) || item.title);
    if (item.seo && item.seo.description) upsertMeta("property", "og:description", item.seo.description);
    const ogImage = toAbs(item.seo && item.seo.ogImage);
    const ogImageEl = ogImage ? upsertMeta("property", "og:image", ogImage) : null;
    const twImageEl = ogImage ? upsertMeta("name", "twitter:image", ogImage) : null;

    // Article schema (JSON-LD) — only emitted on publication, so drafts
    // are never surfaced to structured-data crawlers. Mirrors the Perspective
    // pattern for consistency across editorial content types.
    let ld = null;
    if (!isDraft) {
      const authorName = item.author || "The Thriving Life 101 Editorial";
      const authorType = authorName.toLowerCase().includes("editorial") ? "Organization" : "Person";
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": item.title,
        "description": (item.seo && item.seo.description) || item.subtitle,
        "image": toAbs((item.seo && item.seo.ogImage) || (item.hero && item.hero.image)),
        "author": { "@type": authorType, "name": authorName },
        "publisher": { "@type": "Organization", "name": "The Thriving Life 101" },
        "datePublished": item.publishedAt,
        "dateModified": item.updatedAt || item.publishedAt,
        "articleSection": item.section || "Community",
        "mainEntityOfPage": url
      });
      document.head.appendChild(ld);
    }

    return () => {
      document.title = originalTitle;
      // Robots and canonical are restored so subsequent routes are not left
      // in a stale state; the header/route change would otherwise persist.
      robots.setAttribute("content", "index, follow, max-image-preview:large");
      canonical.setAttribute("href", "https://www.thethrivinglife101.com/");
      if (ogImageEl && ogImageEl.parentNode) ogImageEl.parentNode.removeChild(ogImageEl);
      if (twImageEl && twImageEl.parentNode) twImageEl.parentNode.removeChild(twImageEl);
      if (ld && ld.parentNode) ld.parentNode.removeChild(ld);
    };
  }, [item]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="founder-loading">
        <p className="font-serif italic text-secondary-custom">Loading feature…</p>
      </div>
    );
  }
  if (error || !item) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 text-center" data-testid="founder-notfound">
        <div className="max-w-md">
          <h1 className="font-serif text-3xl text-primary-custom">Feature not found</h1>
          <p className="mt-4 font-serif italic text-secondary-custom">The path you followed no longer leads here.</p>
          <Link to="/" className="mt-8 inline-block text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)]">Return home</Link>
        </div>
      </div>
    );
  }

  const isDraft = item.status !== "published";
  const isPortrait = (item.hero && item.hero.aspect) === "portrait";

  const returnTo = item.returnTo && item.returnTo.path ? item.returnTo : null;

  return (
    <article className="w-full" data-testid="founder-feature-page">
      {returnTo && (
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10">
          <Link
            to={returnTo.path}
            data-testid="founder-back-link"
            className="inline-flex items-center space-x-2 text-[10px] font-sans tracking-[0.2em] uppercase text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            <span>{returnTo.backLabel || "Back"}</span>
          </Link>
        </div>
      )}
      {isDraft && (
        <div className="max-w-4xl mx-auto px-6 md:px-12 pt-10" data-testid="founder-draft-banner">
          <div className="border border-[var(--accent-bronze)]/50 px-6 py-4 text-center">
            <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold">
              Editorial preview — draft
            </span>
          </div>
        </div>
      )}

      {/* Title block. Restrained, centred, plenty of air. */}
      <header className="max-w-3xl mx-auto px-6 md:px-12 pt-20 md:pt-28 text-center">
        {(item.series || item.seriesNumber) && (
          <p className="text-[10px] font-sans tracking-[0.4em] uppercase text-[var(--accent-bronze)] font-semibold">
            {[item.series, item.seriesNumber].filter(Boolean).join(" · ")}
          </p>
        )}
        <h1
          className="mt-6 md:mt-8 font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight text-primary-custom leading-[1.05]"
          data-testid="founder-title"
        >
          {item.title}
        </h1>
        {item.subtitle && (
          <p
            className="mt-8 md:mt-10 font-serif italic text-lg md:text-xl text-secondary-custom leading-relaxed font-light max-w-2xl mx-auto"
            data-testid="founder-subtitle"
          >
            {item.subtitle}
          </p>
        )}

        {/* Editorial meta: reading time + article-length audio narration.
            Reading time is calculated from the article body. Audio narration
            uses the existing multilingual TTS backend and follows the active
            interface language. */}
        <div className="mt-8 md:mt-10 flex flex-col items-center gap-3">
          <div className="inline-flex items-center gap-3">
            <ReadingTime
              text={(item.body || []).map((b) => b && b.text).filter(Boolean)}
              minutes={typeof item.readMinutes === "number" ? item.readMinutes : undefined}
              testId="founder-reading-time"
              pageType="founder_feature"
              pageSlug={item.slug}
            />
            {item.publicationLabel && (
              <>
                <span className="w-1 h-1 rounded-full bg-secondary-custom/50" aria-hidden="true" />
                <span
                  data-testid="founder-opening-date"
                  className="text-[10px] font-sans tracking-[0.25em] uppercase text-secondary-custom"
                >
                  {item.publicationLabel}
                </span>
              </>
            )}
          </div>
          <ArticleAudioPlayer
            text={[
              item.title,
              item.subtitle,
              ...((item.body || []).map((b) => b && b.text).filter(Boolean)),
            ].filter(Boolean).join(" ")}
            pageType="founder_feature"
            pageSlug={item.slug}
            readingMinutes={estimateReadingMinutes((item.body || []).map((b) => b && b.text).filter(Boolean))}
            preGeneratedAudio={item.preGeneratedAudio}
            testId="founder-listen-article"
          />
          <p className="text-[10px] font-sans tracking-[0.2em] uppercase text-secondary-custom/70 max-w-md leading-relaxed">
            {tListen}
          </p>
        </div>
      </header>

      {/* Hero portrait. Introduced immediately beneath the title, with generous
          whitespace on all sides. Portrait orientation is preserved (no crop).
          The photograph — not the prose — is what introduces the woman before
          the brand is introduced by name. */}
      {item.hero && item.hero.image && (
        <figure
          className="max-w-2xl mx-auto px-6 md:px-12 mt-16 md:mt-24"
          data-testid="founder-hero-figure"
        >
          <div className="w-full flex justify-center">
            <picture>
              {item.hero.imageWebp && (
                <source srcSet={item.hero.imageWebp} type="image/webp" />
              )}
              <img
                src={item.hero.image}
                alt={item.hero.alt || ""}
                loading="eager"
                decoding="async"
                className={`${isPortrait ? "w-full h-auto max-h-[85vh] object-contain" : "w-full h-auto"} shadow-sm`}
                data-testid="founder-hero-image"
              />
            </picture>
          </div>
          {(item.hero.caption || (item.subject && item.subject.name)) && (
            <figcaption className="mt-8 md:mt-10 text-center font-serif italic text-sm md:text-base text-secondary-custom leading-relaxed">
              {item.hero.caption || `${item.subject.name}, ${item.subject.role || ""}`.trim()}
              {item.hero.credit && (
                <span className="block mt-3 text-[10px] font-sans not-italic tracking-[0.25em] uppercase text-secondary-custom/60">
                  Photograph · {item.hero.credit}
                </span>
              )}
            </figcaption>
          )}
        </figure>
      )}

      {/* Body. Centred, single-column, editorial line length. */}
      <div className="max-w-2xl mx-auto px-6 md:px-12 mt-24 md:mt-32 mb-24 md:mb-32" data-testid="founder-body">
        {(item.body || []).map((block, idx) => (
          <Block key={idx} block={block} />
        ))}

        {/* Discreet founder & brand links — reusable data-driven component.
            Renders nothing if the article has no brandLinks. The visual
            treatment is defined once inside FounderLinks; publishing a new
            Founder Feature never touches this file. */}
        <FounderLinks
          brandLinks={item.brandLinks}
          featureId={item.featureId}
          founder={item.subject && item.subject.name}
          brand={item.subject && item.subject.brand}
        />

        {/* Byline / colophon */}
        {(item.author || item.publicationLabel || item.publishedAt) && (
          <div className="mt-20 pt-10 border-t border-subtle-custom text-center">
            {item.author && (
              <p className="text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom" data-testid="founder-byline-author">
                {item.author}
              </p>
            )}
            {item.publicationLabel && (
              <p className="mt-3 text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom" data-testid="founder-byline-date">
                {item.publicationLabel}
              </p>
            )}
          </div>
        )}

        <div className="mt-14 text-center">
          <Link
            to={returnTo ? returnTo.path : "/"}
            data-testid="founder-return-home"
            className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.3em] uppercase text-primary-custom hover:text-[var(--accent-bronze)] transition-colors"
          >
            <ChevronLeft className="w-3 h-3" />
            <span>{returnTo ? (returnTo.label || "Return") : "Return home"}</span>
          </Link>
        </div>
      </div>
    </article>
  );
}
